import { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  PHASE_STEP_SIGNALS,
  type JourneyPhaseStep,
  type PhaseStatus,
  type PhaseStepSignal,
} from "@care-by-ella/contracts";
import { useAuth } from "@/auth/AuthContext";
import { UnauthorizedError } from "@/lib/clinician-api";
import {
  archiveJourneyPhase,
  createJourneyPhase,
  fetchJourneyPhase,
  publishJourneyPhase,
  updateJourneyPhase,
} from "@/lib/journey-phases-api";

// TES-209 — the clinical phase editor. A phase is a named, ordered narrative
// stage with localized title/focus and an ordered list of steps; each step is
// auto-completed by a derived signal. Phases are unversioned: save edits in
// place, then publish/archive to flip status.

type EditablePhase = {
  emoji: string;
  titleSv: string;
  titleEn: string;
  focusSv: string;
  focusEn: string;
  order: number;
  status: PhaseStatus;
  steps: JourneyPhaseStep[];
};

const EMPTY: EditablePhase = {
  emoji: "",
  titleSv: "",
  titleEn: "",
  focusSv: "",
  focusEn: "",
  order: 0,
  status: "draft",
  steps: [],
};

export function JourneyPhaseEditorRoute() {
  const { signOut } = useAuth();
  const navigate = useNavigate();
  const { key: routeKey } = useParams<{ key: string }>();
  const isNew = !routeKey || routeKey === "new";

  const [keyInput, setKeyInput] = useState("");
  const [phase, setPhase] = useState<EditablePhase | null>(null);
  const [busy, setBusy] = useState<"save" | "publish" | "archive" | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const handleErr = useCallback(
    (e: unknown) => {
      if (e instanceof UnauthorizedError) {
        void signOut();
        return;
      }
      setError(e instanceof Error ? e.message : String(e));
    },
    [signOut],
  );

  const load = useCallback(() => {
    setError(null);
    if (isNew) {
      setPhase({ ...EMPTY });
      return;
    }
    fetchJourneyPhase(routeKey!)
      .then((p) =>
        setPhase({
          emoji: p.emoji ?? "",
          titleSv: p.title.sv,
          titleEn: p.title.en,
          focusSv: p.focus.sv,
          focusEn: p.focus.en,
          order: p.order,
          status: p.status,
          steps: p.steps,
        }),
      )
      .catch(handleErr);
  }, [isNew, routeKey, handleErr]);

  useEffect(load, [load]);

  function patch(p: Partial<EditablePhase>) {
    setPhase((d) => (d ? { ...d, ...p } : d));
  }

  function patchStep(index: number, p: Partial<JourneyPhaseStep>) {
    setPhase((d) => {
      if (!d) return d;
      const steps = d.steps.slice();
      steps[index] = { ...steps[index]!, ...p };
      return { ...d, steps };
    });
  }

  function patchStepLabel(index: number, locale: "sv" | "en", value: string) {
    setPhase((d) => {
      if (!d) return d;
      const steps = d.steps.slice();
      steps[index] = {
        ...steps[index]!,
        label: { ...steps[index]!.label, [locale]: value },
      };
      return { ...d, steps };
    });
  }

  function addStep() {
    setPhase((d) =>
      d
        ? {
            ...d,
            steps: [
              ...d.steps,
              { key: "", label: { sv: "", en: "" }, completedBy: "manual" },
            ],
          }
        : d,
    );
  }

  function removeStep(index: number) {
    setPhase((d) =>
      d ? { ...d, steps: d.steps.filter((_, i) => i !== index) } : d,
    );
  }

  function moveStep(index: number, dir: -1 | 1) {
    setPhase((d) => {
      if (!d) return d;
      const steps = d.steps.slice();
      const j = index + dir;
      if (j < 0 || j >= steps.length) return d;
      [steps[index], steps[j]] = [steps[j]!, steps[index]!];
      return { ...d, steps };
    });
  }

  function payload(status: PhaseStatus) {
    if (!phase) throw new Error("no phase");
    return {
      key: isNew ? keyInput : routeKey!,
      emoji: phase.emoji || null,
      title: { sv: phase.titleSv, en: phase.titleEn },
      focus: { sv: phase.focusSv, en: phase.focusEn },
      steps: phase.steps,
      order: phase.order,
      status,
    };
  }

  async function onSave() {
    if (!phase) return;
    setBusy("save");
    setError(null);
    setNotice(null);
    try {
      if (isNew) {
        const created = await createJourneyPhase(payload("draft"));
        setNotice("Fas skapad.");
        navigate(`/journey-phases/${encodeURIComponent(created.key)}`);
        return;
      }
      // Keep the current status on a plain save; publish/archive are explicit.
      await updateJourneyPhase(routeKey!, payload(phase.status));
      setNotice("Sparat.");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  async function onPublish() {
    if (isNew || !routeKey) return;
    setBusy("publish");
    setError(null);
    setNotice(null);
    try {
      const p = await publishJourneyPhase(routeKey);
      patch({ status: p.status });
      setNotice("Publicerad — fasen visas nu för patienter.");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  async function onArchive() {
    if (isNew || !routeKey) return;
    setBusy("archive");
    setError(null);
    try {
      await archiveJourneyPhase(routeKey);
      navigate("/journey-phases");
    } catch (e) {
      handleErr(e);
    } finally {
      setBusy(null);
    }
  }

  if (!phase) {
    return <div className="text-textSecondary text-sm">Hämtar…</div>;
  }

  const field =
    "border border-surfaceMuted rounded-md p-sm text-sm focus:outline-none focus:border-brand";
  const labelCls = "text-xs uppercase tracking-wide text-textSecondary";

  return (
    <div className="flex flex-col gap-md max-w-3xl">
      <div className="flex items-baseline justify-between">
        <h1 className="font-display text-3xl tracking-tight">
          {isNew ? "Ny fas" : phase.titleSv || routeKey}
        </h1>
        {!isNew ? (
          <span className="text-xs text-textSecondary">
            Status: {phase.status}
          </span>
        ) : null}
      </div>

      {error ? (
        <div className="bg-danger/10 border border-danger/30 text-danger rounded-md p-sm text-sm">
          {error}
        </div>
      ) : null}
      {notice ? (
        <div className="bg-success/10 border border-success/30 text-success rounded-md p-sm text-sm">
          {notice}
        </div>
      ) : null}

      <div className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-md">
        {isNew ? (
          <label className="flex flex-col gap-xs">
            <span className={labelCls}>Nyckel (slug)</span>
            <input
              value={keyInput}
              onChange={(e) => setKeyInput(e.target.value)}
              placeholder="t.ex. know_your_body"
              className={`${field} font-mono`}
            />
          </label>
        ) : null}

        <div className="grid grid-cols-[80px_1fr] gap-sm">
          <label className="flex flex-col gap-xs">
            <span className={labelCls}>Emoji</span>
            <input
              value={phase.emoji}
              onChange={(e) => patch({ emoji: e.target.value })}
              placeholder="🌱"
              className={field}
            />
          </label>
          <label className="flex flex-col gap-xs">
            <span className={labelCls}>Ordning</span>
            <input
              type="number"
              value={phase.order}
              onChange={(e) => patch({ order: Number(e.target.value) })}
              className={field}
            />
          </label>
        </div>

        <div className="grid grid-cols-2 gap-sm">
          <label className="flex flex-col gap-xs">
            <span className={labelCls}>Namn (sv)</span>
            <input
              value={phase.titleSv}
              onChange={(e) => patch({ titleSv: e.target.value })}
              className={field}
            />
          </label>
          <label className="flex flex-col gap-xs">
            <span className={labelCls}>Namn (en)</span>
            <input
              value={phase.titleEn}
              onChange={(e) => patch({ titleEn: e.target.value })}
              className={field}
            />
          </label>
          <label className="flex flex-col gap-xs">
            <span className={labelCls}>Fokus (sv)</span>
            <textarea
              value={phase.focusSv}
              onChange={(e) => patch({ focusSv: e.target.value })}
              className={`${field} min-h-[60px]`}
            />
          </label>
          <label className="flex flex-col gap-xs">
            <span className={labelCls}>Fokus (en)</span>
            <textarea
              value={phase.focusEn}
              onChange={(e) => patch({ focusEn: e.target.value })}
              className={`${field} min-h-[60px]`}
            />
          </label>
        </div>
      </div>

      {/* Steps */}
      <div className="bg-surface border border-surfaceMuted rounded-md p-md flex flex-col gap-sm">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg">Steg</h2>
          <button
            type="button"
            onClick={addStep}
            className="text-brand hover:underline text-sm"
          >
            + Lägg till steg
          </button>
        </div>
        {phase.steps.length === 0 ? (
          <p className="text-textSecondary text-sm">Inga steg än.</p>
        ) : (
          <ol className="flex flex-col gap-sm">
            {phase.steps.map((step, i) => (
              // Index key: a step's own `key` is editable + may be blank/dup
              // while authoring, so it's not stable. Inputs are controlled so
              // values stay correct; the only cost is caret focus on reorder.
              <li
                key={i}
                className="border border-surfaceMuted rounded-md p-sm flex flex-col gap-xs"
              >
                <div className="flex items-center gap-sm">
                  <span className="text-textSecondary tabular-nums">
                    {i + 1}.
                  </span>
                  <input
                    value={step.key}
                    onChange={(e) => patchStep(i, { key: e.target.value })}
                    placeholder="nyckel"
                    className={`${field} font-mono w-40`}
                  />
                  <select
                    value={step.completedBy}
                    onChange={(e) =>
                      patchStep(i, {
                        completedBy: e.target.value as PhaseStepSignal,
                      })
                    }
                    className={field}
                  >
                    {PHASE_STEP_SIGNALS.map((s) => (
                      <option key={s} value={s}>
                        {s}
                      </option>
                    ))}
                  </select>
                  <span className="ml-auto flex items-center gap-xs">
                    <button
                      type="button"
                      onClick={() => moveStep(i, -1)}
                      disabled={i === 0}
                      className="text-textSecondary hover:text-textPrimary disabled:opacity-30"
                      aria-label="Flytta upp"
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      onClick={() => moveStep(i, 1)}
                      disabled={i === phase.steps.length - 1}
                      className="text-textSecondary hover:text-textPrimary disabled:opacity-30"
                      aria-label="Flytta ner"
                    >
                      ↓
                    </button>
                    <button
                      type="button"
                      onClick={() => removeStep(i)}
                      className="text-textSecondary hover:text-danger"
                    >
                      Ta bort
                    </button>
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-sm pl-md">
                  <input
                    value={step.label.sv}
                    onChange={(e) => patchStepLabel(i, "sv", e.target.value)}
                    placeholder="Etikett (sv)"
                    className={field}
                  />
                  <input
                    value={step.label.en}
                    onChange={(e) => patchStepLabel(i, "en", e.target.value)}
                    placeholder="Label (en)"
                    className={field}
                  />
                </div>
              </li>
            ))}
          </ol>
        )}
      </div>

      <div className="flex items-center gap-sm">
        <button
          type="button"
          onClick={onSave}
          disabled={busy !== null || (isNew && !keyInput)}
          className="px-md py-sm rounded-md bg-brand text-white text-sm hover:bg-brand/90 disabled:opacity-50"
        >
          {busy === "save" ? "Sparar…" : isNew ? "Skapa" : "Spara"}
        </button>
        {!isNew ? (
          <>
            <button
              type="button"
              onClick={onPublish}
              disabled={busy !== null || phase.status === "published"}
              className="px-md py-sm rounded-md border border-brand text-brand text-sm hover:bg-brand/5 disabled:opacity-50"
            >
              {busy === "publish" ? "Publicerar…" : "Publicera"}
            </button>
            <button
              type="button"
              onClick={onArchive}
              disabled={busy !== null}
              className="ml-auto px-md py-sm rounded-md border border-surfaceMuted text-textSecondary text-sm hover:text-danger hover:border-danger disabled:opacity-50"
            >
              Arkivera
            </button>
          </>
        ) : null}
      </div>
    </div>
  );
}
